import * as THREE from "three";

// Shared geometry for explosion particles - created once and reused
let sharedParticleGeometry: THREE.SphereGeometry | null = null;

// Perf: scratch vector to avoid per-particle per-frame allocation (20 particles
// x 60fps = 1200 Vector3/s per explosion straight into GC).
const _explosionStep = new THREE.Vector3();

function getSharedParticleGeometry(): THREE.SphereGeometry {
  if (!sharedParticleGeometry) {
    // Use smaller segment count for better performance (6,6 instead of 8,8)
    sharedParticleGeometry = new THREE.SphereGeometry(1, 6, 6);
  }
  return sharedParticleGeometry;
}

/**
 * Tworzy efekt wybuchu w podanej pozycji
 * @param scene Scena Three.js
 * @param position Pozycja wybuchu
 * @param scale Skala wybuchu (domyślnie 1.0)
 * @returns THREE.Group zawierający wszystkie elementy wybuchu
 */
export function createExplosion(scene: THREE.Scene, position: THREE.Vector3, scale: number = 1.0): THREE.Group {
  // Kontener na wszystkie elementy wybuchu
  const explosionGroup = new THREE.Group();
  explosionGroup.position.copy(position);
  // Add a flag to track if the explosion is still active
  explosionGroup.userData.isActive = true;
  scene.add(explosionGroup);

  try {
    // Efekt wybuchu - światło
    const explosionLight = new THREE.PointLight(0xff5500, 5 * scale, 10 * scale);
    explosionGroup.add(explosionLight);

    // Efekt wybuchu - cząsteczki
    const particleCount = Math.floor(20 * scale);
    const particles: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[] = [];

    // Use shared geometry for all particles
    const sharedGeometry = getSharedParticleGeometry();

    for (let i = 0; i < particleCount; i++) {
      const particleMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(0.8 + Math.random() * 0.2, 0.3 + Math.random() * 0.4, 0.0),
        transparent: true,
        opacity: 0.8,
      });
      // Use shared geometry and scale via mesh.scale instead of creating new geometry
      const particle = new THREE.Mesh(sharedGeometry, particleMaterial);
      const particleScale = (0.05 + Math.random() * 0.1) * scale;
      particle.scale.setScalar(particleScale);

      // Losowy kierunek rozrzutu
      const direction = new THREE.Vector3(Math.random() * 2 - 1, Math.random() * 2, Math.random() * 2 - 1).normalize();

      // Zapisz kierunek i prędkość jako właściwości cząsteczki
      particle.userData = {
        direction,
        speed: (0.05 + Math.random() * 0.1) * scale,
      };

      explosionGroup.add(particle);
      particles.push(particle);
    }

    // Animacja wybuchu
    let explosionTime = 0;
    let animationFrameId: number;
    const animateExplosion = () => {
      // Check if the explosion group is still in the scene and active
      if (!explosionGroup.parent || !explosionGroup.userData.isActive) {
        // Cleanup if removed from scene
        cancelAnimationFrame(animationFrameId);
        cleanupExplosion();
        return;
      }

      explosionTime += 0.016;

      // Zmniejszaj intensywność światła
      explosionLight.intensity = Math.max(0, 5 * scale - explosionTime * 5);

      // Aktualizuj pozycje cząsteczek (bez alokacji - ten sam wygląd)
      for (const particle of particles) {
        const dir = particle.userData.direction as THREE.Vector3;
        const spd = particle.userData.speed as number;

        particle.position.add(
          _explosionStep.set(dir.x * spd, dir.y * spd - 0.01 * scale, dir.z * spd),
        );

        // Zmniejszaj przezroczystość
        (particle.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.8 - explosionTime * 0.8);
      }

      // Zakończ animację po 1 sekundzie
      if (explosionTime > 1) {
        // Cancel animation frame
        cancelAnimationFrame(animationFrameId);
        cleanupExplosion();
        return;
      }
      animationFrameId = requestAnimationFrame(animateExplosion);
    };

    // Add cleanup function
    const cleanupExplosion = () => {
      explosionGroup.userData.isActive = false;
      // Usuń wszystkie elementy z grupy
      while (explosionGroup.children.length > 0) {
        const child = explosionGroup.children[0];
        explosionGroup.remove(child);
        if (child instanceof THREE.Mesh) {
          // Don't dispose shared geometry - only dispose materials
          child.material.dispose();
        }
      }
      // Usuń grupę ze sceny if still there
      if (explosionGroup.parent) {
        scene.remove(explosionGroup);
      }
    };

    // Add cleanup method to the group for external use
    explosionGroup.userData.cleanup = cleanupExplosion;

    animationFrameId = requestAnimationFrame(animateExplosion);
    return explosionGroup;
  } catch (error) {
    console.error("Error creating explosion effect:", error);
    scene.remove(explosionGroup);
    return explosionGroup; // Return empty group in case of error
  }
}
